import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useGroups } from "@nju/data-access-portal-dashboard";
import { useMemberOrchestrator } from "@nju/data-access-portal-member";
import { useOffers } from "@nju/data-access-portal-offers";
import { pushAddToCartAdditionalPackageEvent } from "@nju/data-layer";
import { newOrderMachine } from "@nju/feature-portal-order";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { A, Panel, Separator, Text } from "@nju/ui";
import { formatPhoneNumber, POSITIVE_DIGITS } from "@nju/utils";
import { parseMegabytesToGigabytes } from "@nju/utils";
import { allowNewNumber } from "./allowNewNumber";

interface ISelectAdditionalPackageProps {
  showBackButton?: boolean;
}

export function SelectAdditionalPackage({
  showBackButton = false,
}: ISelectAdditionalPackageProps) {
  const { mobile } = useMemberOrchestrator();
  const groups = useGroups();
  const router = useRouter();
  const showAddNewNumberButton = allowNewNumber({
    groups,
  });
  const offerId = mobile?.offerId;
  const {
    mobile: { indefiniteOffers },
  } = useOffers();
  const offer = indefiniteOffers.find((offer) => {
    return offer.display;
  });

  const { userId } = portalRoutes.mojeKonto.uslugiDodatkowe.index
    .input(router.query)
    .unwrap();

  const [current, send] = useMachine(newOrderMachine, {
    actions: {
      onNewOrderSuccess: () => {
        router.push(portalRoutes.wybierak.path());
      },
      onAnotherOrderInProgress: () => {
        router.push(portalRoutes.zamowienieWTrakcie.get());
      },
    },
  });

  const { context } = current;

  const handleNewOrder = React.useCallback(() => {
    send({ type: "NEW_ORDER" });
  }, [send]);

  return (
    <Panel testId="mv-additional-package-panel">
      {showBackButton && (
        <Link
          href={portalRoutes.mojeKonto.index.get({
            mode: "mobile",
            ...(userId && { userId }),
          })}
          passHref={true}
        >
          <A
            className="mb-3"
            display="inlineFlex"
            size="inline"
            variant="neutral"
          >
            <Icon height={24} name="arrow-left" tone="accent" width={21} />
            <Text className="ml-2" size="s" tone="accent">
              wstecz
            </Text>
          </A>
        </Link>
      )}
      <Text
        display="block"
        size={{
          "@onlyMobile": "keyS",
          "@fromTablet": "keyM",
        }}
        weight="bold"
      >
        usługi dodatkowe
      </Text>
      <div className="my-3">
        <Text> dodajesz usługi dodatkowe dla numeru:</Text>
        <Separator size="none" tone="invisible" />
        <div className="text-size-18 text-cerulean font-bold">
          {formatPhoneNumber(
            mobile?.verifiedMsisdn,
            POSITIVE_DIGITS,
            " ",
            "",
            true,
          )}
        </div>
      </div>
      <div className="mt-5 flex items-center gap-4">
        <Icon height={24} name="cart-plus" tone="accent" width={27} />
        <Text size="l" weight="bold">
          dodatkowe GB w kraju i UE
        </Text>
      </div>
      <Separator size="s" />
      <Text
        size={{
          "@onlyMobile": "m",
          "@fromTablet": "l",
        }}
      >
        Gdy potrzebujesz więcej internetu.
      </Text>
      <Link
        href={portalRoutes.mojeKonto.uslugiDodatkowe.pakietGb.get({
          ...(userId && { userId }),
        })}
        passHref={true}
      >
        <A
          display="block"
          onClick={() => {
            pushAddToCartAdditionalPackageEvent({
              name: "Pakiet GB",
              category: "VAS/Pakiety GB",
            });
          }}
          size="inline"
          variant="neutral"
        >
          <div
            className="rounded-[12px] only-mobile:p-3 p-5 border-2 border-geyser mt-2"
            data-cy="additional-package-gb-panel"
          >
            <Text display="block" size="l" weight="bold">
              pakiet GB
            </Text>
            <Text className="mt-1 mb-6" display="block">
              pakiet dodatkowych 9 GB za {context.productOfferPrice} zł do
              wykorzystania w Polsce (w tym{" "}
              {parseMegabytesToGigabytes(context.productOfferGrantValue)} GB w
              UE)
            </Text>
            <div className="flex items-center gap-2">
              <Text size="s" tone="accent">
                sprawdź
              </Text>
              <Icon height={19} name="arrow-right" tone="accent" width={19} />
            </div>
          </div>
        </A>
      </Link>
      <div className="mt-6 flex items-center gap-4">
        <Icon height={24} name="plane-departure" tone="accent" width={30} />
        <Text size="l" weight="bold">
          usługi międzynarodowe
        </Text>
      </div>
      <Separator size="s" />
      <Text
        size={{
          "@onlyMobile": "m",
          "@fromTablet": "l",
        }}
      >
        Gdy planujesz wyjazd za granicę lub masz bliskich poza krajem.
      </Text>
      <div className="grid grid-cols-1 from-tablet:grid-cols-2 gap-2 mt-2">
        <Link
          href={portalRoutes.mojeKonto.uslugiDodatkowe.roaming.index.get({
            ...(userId && { userId }),
          })}
          passHref={true}
        >
          <A
            display="block"
            onClick={() => {
              pushAddToCartAdditionalPackageEvent({
                name: "Pakiety roamingowe",
                category: "VAS/Pakiety Roamingowe",
              });
            }}
            size="inline"
            variant="neutral"
          >
            <div
              className="rounded-[12px] only-mobile:p-3 p-5 border-2 border-geyser mt-2"
              data-cy="roaming-package-panel"
            >
              <Text display="block" size="m" tone="accent" weight="bold">
                gdy jesteś za granicą
              </Text>
              <Text display="block" size="l" weight="bold">
                pakiety roamingowe
              </Text>
              <Text className="mt-1 mb-6" display="block">
                dodatkowe GB, SMS, MMS i minuty do wykorzystania za granicą
              </Text>
              <div className="flex items-center gap-2 cursor-pointer">
                <Text size="s" tone="accent">
                  sprawdź
                </Text>
                <Icon height={19} name="arrow-right" tone="accent" width={19} />
              </div>
            </div>
          </A>
        </Link>
        <Link
          href={portalRoutes.mojeKonto.uslugiDodatkowe.rozmowyMiedzynarodowe.index.get(
            { ...(userId && { userId }) },
          )}
          passHref={true}
        >
          <A
            display="block"
            onClick={() => {
              pushAddToCartAdditionalPackageEvent({
                name: "Rozmowy międzynarodowe",
                category: "VAS/Rozmowy Międzynarodowe",
              });
            }}
            size="inline"
            variant="neutral"
          >
            <div
              className="rounded-[12px] only-mobile:p-3 p-5 border-2 border-geyser mt-2"
              data-cy="international-package-panel"
            >
              <Text display="block" size="m" tone="accent" weight="bold">
                gdy jesteś w Polsce
              </Text>
              <Text display="block" size="l" weight="bold">
                rozmowy międzynarodowe
              </Text>
              <Text className="mt-1 mb-6" display="block">
                dodatkowe połączenia między Polską a wybranym krajem
              </Text>
              <div className="flex items-center gap-2 cursor-pointer">
                <Text size="s" tone="accent">
                  sprawdź
                </Text>
                <Icon height={19} name="arrow-right" tone="accent" width={19} />
              </div>
            </div>
          </A>
        </Link>
      </div>
      {!!showAddNewNumberButton && offerId && (
        <>
          <Separator size="s" tone="invisible" />
          <Text
            display="block"
            size={{
              "@onlyMobile": "keyS",
              "@fromDesktop": "keyM",
            }}
            weight="bold"
          >
            numery dodatkowe
          </Text>
          <Separator size="xs" tone="invisible" />
          <button
            className="w-full"
            data-cy="additional-numbers-box"
            onClick={handleNewOrder}
          >
            <div className="rounded-[12px] only-mobile:p-3 p-5 border-2 border-geyser mt-2">
              <Text display="block" size="l" weight="bold">
                nju subskrypcja
              </Text>
              <Text className="mt-1 mb-6" display="block">
                na jednym koncie możesz mieć maksymalnie 5 numerów - każdy
                kolejny za {offer?.priceDiscountedAfterMobile} zł
              </Text>
              <div className="flex items-center gap-2">
                <Text size="s" tone="accent">
                  dodaj numer
                </Text>
                <Icon height={19} name="arrow-right" tone="accent" width={19} />
              </div>
            </div>
          </button>
        </>
      )}
    </Panel>
  );
}
